import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Building2, Database, Globe, User, Key, Server, RefreshCw, Upload, Image, Video, Tag, Hash, Settings, History, Lock, Eye, EyeOff } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import AuditLogTable from './AuditLogTable';
import TomaStatesPanel from './TomaStatesPanel';
import ConfigSectionShell, { SectionTab } from '../common/ConfigSectionShell';

const normalizeType = (t?: string): 'demo' | 'client' =>
  t === 'demo' || t === 'demo_customer' ? 'demo' : 'client';

// T-1 (rediseño, D1): clave REUSADA tomaDisplayPolicy. Traduce valores legacy
// (bronze_to_gold / wait_for_catalog) a los nuevos (sin_catalogado / con_catalogado).
const normalizeTomaDisplayPolicy = (p?: string): 'sin_catalogado' | 'con_catalogado' => {
  if (p === 'con_catalogado' || p === 'wait_for_catalog') return 'con_catalogado';
  return 'sin_catalogado'; // default + bronze_to_gold + sin_catalogado
};

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Cuando true, renderiza inline (sin overlay/fixed) — para usar como página dedicada. */
  inline?: boolean;
  /**
   * Slice del formulario a renderizar (inline). Reusa el MISMO formData + handleSubmit.
   * - undefined → form completo (modal legacy desde ClientCard).
   * - 'datosBasicos' → General: Info Básica + Video + Funcionalidades (Tipo/Estado/Tags/SKU).
   * - 'origen' → Configuración de Base de Datos (tab Origen de "Tomas de Datos").
   * - 'visibilidad' → Mostrar Tomas Nuevas + estados por-toma (tab Visibilidad).
   * Con 'origen'/'visibilidad' NO se envuelve en ConfigSectionShell (el shell lo provee la sección padre).
   */
  section?: 'datosBasicos' | 'origen' | 'visibilidad';
  client: {
    uid: string;
    name: string;
    display_name?: string;
    database?: {
      host: string;
      user: string;
      database: string;
      password: string;
      port?: number;
    };
    logo?: string;
    logoHeight?: number;
    welcomeVideo?: string;
    active?: boolean;
    client_type?: string;
    enable_tags?: boolean;
    enable_sku?: boolean;
    tomaDisplayPolicy?: string;
  };
  onSave: (patch?: Record<string, any>) => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({ isOpen, onClose, client, onSave, inline = false, section }) => {
  const { userData, currentUser } = useAuth();
  const isSuperAdmin = userData?.user_type === 'superadmin';
  // Origen (config de BD) es editable SOLO para superadmin; el resto lo ve read-only
  // y la contraseña queda enmascarada sin ojo de revelado.
  const origenReadOnly = !isSuperAdmin;
  const [showDbPassword, setShowDbPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'history'>('general');
  // Qué bloques del form mostrar. section == null → todos (modal legacy).
  const showBasico = section == null || section === 'datosBasicos';
  const showOrigen = section == null || section === 'origen';
  const showVisibilidad = section == null || section === 'visibilidad';
  const [originalActive, setOriginalActive] = useState<boolean>(true);
  const [originalType, setOriginalType] = useState<'client' | 'demo'>('client');
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    database: {
      host: '',
      user: '',
      database: '',
      password: '',
      port: 3306
    },
    logo: '',
    logoHeight: 50,
    welcomeVideo: '',
    active: true,
    client_type: 'client' as 'client' | 'demo',
    enable_tags: true,
    enable_sku: false,
    // T-1 (rediseño) "Mostrar Tomas Nuevas". Clave reusada tomaDisplayPolicy con
    // valores nuevos: 'sin_catalogado' (default) muestra toda toma al instante;
    // 'con_catalogado' sólo muestra tomas cuyo estado por-toma es con_catalogado.
    tomaDisplayPolicy: 'sin_catalogado'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (client) {
      const initialActive = client.active !== undefined ? client.active : true;
      const initialType = normalizeType(client.client_type);
      setOriginalActive(initialActive);
      setOriginalType(initialType);
      setActiveTab('general');
      setFormData({
        name: client.name || '',
        display_name: client.display_name || client.name || '',
        database: {
          host: client.database?.host || '',
          user: client.database?.user || '',
          database: client.database?.database || '',
          password: client.database?.password || '',
          port: client.database?.port || 3306
        },
        logo: client.logo || '',
        logoHeight: client.logoHeight || 50,
        welcomeVideo: (client as any).welcomeVideo || '',
        active: initialActive,
        client_type: initialType,
        enable_tags: client.enable_tags !== undefined ? client.enable_tags : true,
        enable_sku: client.enable_sku !== undefined ? client.enable_sku : false,
        // T-1 (rediseño): clave REUSADA tomaDisplayPolicy (D1). Valores nuevos
        // sin_catalogado / con_catalogado; normalizamos legacy en la hidratación.
        tomaDisplayPolicy: normalizeTomaDisplayPolicy((client as any).tomaDisplayPolicy)
      });
    }
  }, [client]);

  if (!isOpen) return null;

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor seleccione un archivo de imagen válido');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    setUploadingLogo(true);
    setError(null);

    try {
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `client-logos/${timestamp}-${file.name}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update the form data with the new logo URL
      setFormData(prev => ({ ...prev, logo: downloadURL }));

    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError('Error al subir el logo. Por favor intente nuevamente.');
    } finally {
      setUploadingLogo(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Por favor seleccione un archivo de video válido');
      return;
    }

    // Validate file size (max 50MB for videos)
    if (file.size > 50 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 50MB');
      return;
    }

    setUploadingVideo(true);
    setError(null);

    try {
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `greeting-videos/${timestamp}-${file.name}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update the form data with the new video URL
      setFormData(prev => ({ ...prev, welcomeVideo: downloadURL }));

    } catch (err: any) {
      console.error('Error uploading video:', err);
      setError('Error al subir el video. Por favor intente nuevamente.');
    } finally {
      setUploadingVideo(false);
      // Reset the file input
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate database configuration
      if (!formData.database.host || formData.database.host === 'demo') {
        setError('Por favor ingrese un host de base de datos válido (no puede ser "demo")');
        setLoading(false);
        return;
      }

      if (!formData.database.user || !formData.database.database || !formData.database.password) {
        setError('Por favor complete todos los campos de la base de datos');
        setLoading(false);
        return;
      }

      // Update client in Firestore (status/type van por endpoints dedicados con audit log)
      const clientRef = doc(db, 'clients', client.uid);
      const updateData: Record<string, any> = {
        name: formData.name,
        display_name: formData.display_name,
        database: formData.database,
        logo: formData.logo,
        logoHeight: formData.logoHeight,
        welcomeVideo: formData.welcomeVideo,
        enable_tags: formData.enable_tags,
        enable_sku: formData.enable_sku,
        tomaDisplayPolicy: formData.tomaDisplayPolicy,
        updated_at: new Date(),
        updated_by: currentUser?.uid || 'admin'
      };

      await updateDoc(clientRef, updateData);

      // Cambios de status/type → endpoint con audit log (solo superadmin)
      if (isSuperAdmin && formData.active !== originalActive) {
        try {
          await api.put(`/api/clients/${client.uid}/status`, { active: formData.active });
        } catch (e: any) {
          console.warn('No se pudo cambiar status via endpoint, fallback a Firestore:', e);
          await updateDoc(clientRef, { active: formData.active });
        }
      }
      if (isSuperAdmin && formData.client_type !== originalType) {
        try {
          await api.put(`/api/clients/${client.uid}/type`, { client_type: formData.client_type });
        } catch (e: any) {
          console.warn('No se pudo cambiar type via endpoint, fallback a Firestore:', e);
          await updateDoc(clientRef, { client_type: formData.client_type });
        }
      }

      // Clear database cache for this client to use new configuration
      setClearingCache(true);
      try {
        await api.post(`/api/admin/clear-cache/${client.uid}`);
      } catch (cacheError) {
        console.warn('⚠️ Could not clear cache, might need manual restart:', cacheError);
      } finally {
        setClearingCache(false);
      }

      onSave({
        name: formData.name,
        display_name: formData.display_name,
        database: formData.database,
        logo: formData.logo,
        logoHeight: formData.logoHeight,
        welcomeVideo: formData.welcomeVideo,
        active: formData.active,
        client_type: formData.client_type,
        enable_tags: formData.enable_tags,
        enable_sku: formData.enable_sku,
        tomaDisplayPolicy: formData.tomaDisplayPolicy,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar el cliente');
    } finally {
      setLoading(false);
    }
  };

  // Tabs de la sección General (inline usa el tab list del shell). El ícono de "General"
  // = Settings, coincide con el item del sidebar.
  const generalTabs: SectionTab[] = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'history', label: 'Historial', icon: History },
  ];

  // Contenido de la tab activa (compartido modal/inline).
  const tabBody =
    activeTab === 'history' ? (
      <div className={inline ? '' : 'p-6'}>
        <AuditLogTable clientId={client.uid} />
      </div>
    ) : (
      <form onSubmit={handleSubmit} className={inline ? 'space-y-6' : 'p-6 space-y-6'}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Information */}
          {showBasico && (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Información Básica
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Interno
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre para Mostrar
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="space-y-4">
              {/* Logo Preview */}
              {formData.logo && (
                <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={formData.logo} 
                    alt="Logo preview" 
                    style={{ height: `${formData.logoHeight}px`, maxHeight: '60px' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {/* Logo Upload Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploadingLogo ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Subir Nuevo Logo
                      </>
                    )}
                  </button>
                  
                  {formData.logo && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Quitar Logo
                    </button>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  <p>• Formatos aceptados: JPG, PNG, SVG, GIF</p>
                  <p>• Tamaño máximo: 5MB</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Image className="w-4 h-4 inline mr-1" />
                    URL del Logo (opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.logo || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, logo: e.target.value }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/logo.png"
                    disabled={uploadingLogo}
                  />
                </div>
              
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altura del Logo (px)
                  </label>
                  <input
                    type="number"
                    value={formData.logoHeight}
                    onChange={(e) => setFormData({ ...formData, logoHeight: parseInt(e.target.value) || 50 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="20"
                    max="100"
                  />
                </div>
              </div>
            </div>

            {/* Welcome Video Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Video className="w-4 h-4" />
                Video de Bienvenida
              </h3>

              {/* Video Preview */}
              {formData.welcomeVideo && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <video
                    src={formData.welcomeVideo}
                    controls
                    className="w-full max-w-md mx-auto rounded-lg"
                    style={{ maxHeight: '300px' }}
                  >
                    Tu navegador no soporta la reproducción de videos.
                  </video>
                </div>
              )}

              {/* Video Upload Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploadingVideo ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Subiendo Video...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Subir Video de Bienvenida
                      </>
                    )}
                  </button>

                  {formData.welcomeVideo && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, welcomeVideo: '' }))}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Quitar Video
                    </button>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  <p>• Formatos aceptados: MP4, MOV, AVI, WebM</p>
                  <p>• Tamaño máximo: 50MB</p>
                  <p>• Este video se mostrará al iniciar sesión los usuarios de este cliente</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Video className="w-4 h-4 inline mr-1" />
                    URL del Video (opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.welcomeVideo || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, welcomeVideo: e.target.value }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/video.mp4"
                    disabled={uploadingVideo}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Database Configuration (Origen) */}
          {showOrigen && (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                Configuración de Base de Datos
              </span>
              {origenReadOnly && (
                <span className="text-xs font-normal text-gray-500 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> solo superadmin
                </span>
              )}
            </h3>
            
            {formData.database.host === 'demo' && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
                <p className="font-medium">⚠️ Configuración Demo Detectada</p>
                <p className="text-sm mt-1">
                  Este cliente tiene una configuración de demo. Por favor, actualice con los datos reales de la base de datos.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Server className="w-4 h-4 inline mr-1" />
                  Host
                </label>
                <input
                  type="text"
                  value={formData.database.host}
                  onChange={(e) => setFormData({
                    ...formData,
                    database: { ...formData.database, host: e.target.value }
                  })}
                  disabled={origenReadOnly}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="ej: localhost, 192.168.1.100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Puerto
                </label>
                <input
                  type="number"
                  value={formData.database.port}
                  onChange={(e) => setFormData({
                    ...formData,
                    database: { ...formData.database, port: parseInt(e.target.value) }
                  })}
                  disabled={origenReadOnly}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="3306"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Database className="w-4 h-4 inline mr-1" />
                  Nombre de Base de Datos
                </label>
                <input
                  type="text"
                  value={formData.database.database}
                  onChange={(e) => setFormData({
                    ...formData,
                    database: { ...formData.database, database: e.target.value }
                  })}
                  disabled={origenReadOnly}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="ej: catalog, tracking_db"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Usuario
                </label>
                <input
                  type="text"
                  value={formData.database.user}
                  onChange={(e) => setFormData({
                    ...formData,
                    database: { ...formData.database, user: e.target.value }
                  })}
                  disabled={origenReadOnly}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="ej: root, admin"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="w-4 h-4 inline mr-1" />
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={isSuperAdmin && showDbPassword ? 'text' : 'password'}
                  value={formData.database.password}
                  onChange={(e) => setFormData({
                    ...formData,
                    database: { ...formData.database, password: e.target.value }
                  })}
                  disabled={origenReadOnly}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${isSuperAdmin ? 'pr-10' : ''}`}
                  placeholder="••••••••"
                  required
                />
                {/* Ojo de revelado: SOLO superadmin (el resto no ve la contraseña). */}
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowDbPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    title={showDbPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-label={showDbPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showDbPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Features Configuration (general — Datos Básicos) */}
          {showBasico && (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configuración de Funcionalidades
            </h3>

            <div className="space-y-3">
              {/* Tipo de cliente (solo superadmin) */}
              <div className={`p-3 rounded-lg border ${isSuperAdmin ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> Tipo
                  </label>
                  {!isSuperAdmin && <span className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> solo superadmin</span>}
                </div>
                <div className="flex gap-4 text-sm">
                  <label className={`flex items-center gap-2 ${!isSuperAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="client_type"
                      value="client"
                      checked={formData.client_type === 'client'}
                      disabled={!isSuperAdmin}
                      onChange={() => setFormData({ ...formData, client_type: 'client' })}
                    />
                    Cliente
                  </label>
                  <label className={`flex items-center gap-2 ${!isSuperAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="client_type"
                      value="demo"
                      checked={formData.client_type === 'demo'}
                      disabled={!isSuperAdmin}
                      onChange={() => setFormData({ ...formData, client_type: 'demo' })}
                    />
                    Demo
                  </label>
                </div>
                {isSuperAdmin && formData.client_type !== originalType && (
                  <p className="mt-2 text-xs text-amber-700">
                    Cambia de panel: {originalType} → {formData.client_type}
                  </p>
                )}
              </div>

              {/* Estado Activo/Inactivo (solo superadmin puede cambiarlo, los demás lo ven) */}
              <div className={`p-3 rounded-lg border ${isSuperAdmin ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">
                    Estado
                  </label>
                  {!isSuperAdmin && <span className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> solo superadmin</span>}
                </div>
                <label className={`mt-2 inline-flex items-center gap-2 ${!isSuperAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    disabled={!isSuperAdmin}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm">{formData.active ? 'Activo (sync auto)' : 'Inactivo (no sync, no aparece en login)'}</span>
                </label>
                {isSuperAdmin && formData.active !== originalActive && (
                  <p className="mt-2 text-xs text-amber-700">
                    {formData.active
                      ? 'Volverá a entrar al sync automático'
                      : 'Saldrá del sync automático. Datos se conservan.'}
                  </p>
                )}
              </div>

              {/* Enable Tags */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="enable_tags"
                  checked={formData.enable_tags}
                  onChange={(e) => setFormData({ ...formData, enable_tags: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <label htmlFor="enable_tags" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    Habilitar Tags
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Si está desactivado, no se mostrarán columnas de tags en la tabla de publicaciones ni en la exportación XLS.
                  </p>
                </div>
              </div>

              {/* Enable SKU */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="enable_sku"
                  checked={formData.enable_sku}
                  onChange={(e) => {
                    setFormData({ ...formData, enable_sku: e.target.checked });
                  }}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <label htmlFor="enable_sku" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <Hash className="w-4 h-4" />
                    Habilitar SKU
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Si está activado, se mostrará una columna SKU editable vinculada a la tabla 'skus' de la base de datos. Los SKU se incluirán en la exportación XLS.
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Visibilidad de Tomas */}
          {showVisibilidad && (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Visibilidad de Tomas
            </h3>

            <div className="space-y-3">
              {/* Mostrar Tomas Nuevas (T-1). Clave REUSADA tomaDisplayPolicy (D1). */}
              <div className="flex items-start gap-2">
                <Database className="w-4 h-4 text-gray-500 mt-1" />
                <div className="flex-1">
                  <label htmlFor="tomaDisplayPolicy" className="text-sm font-medium text-gray-700">
                    Mostrar Tomas Nuevas
                  </label>
                  <select
                    id="tomaDisplayPolicy"
                    value={formData.tomaDisplayPolicy}
                    onChange={(e) => setFormData({ ...formData, tomaDisplayPolicy: e.target.value })}
                    className="mt-1 block w-full max-w-sm text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="sin_catalogado">Sin catalogado (mostrar al instante)</option>
                    <option value="con_catalogado">Con catalogado (mostrar sólo tomas catalogadas)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    "Sin catalogado": la toma aparece en la lista apenas se carga (comportamiento histórico).
                    "Con catalogado": la toma nueva no aparece hasta que el catalogado termina de procesarla.
                  </p>
                </div>
              </div>

              {/* T-1 §6.2: estado por-toma editable (con_catalogado / en_catalogado / sin_catalogado). */}
              {client?.uid && (
                <TomaStatesPanel clientId={client.uid} />
              )}
            </div>
          </div>
          )}

          {/* Action Buttons.
              T-H: el botón "Árbol de categorías" se movió a la sección Config → Categorías
              (TaxonomyEditor inline). Acá sólo quedan Cancelar + Guardar. */}
          <div className="flex justify-end items-center gap-3 pt-4 border-t">
          <div className="flex gap-3">
            {!inline && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading || clearingCache}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {clearingCache ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Limpiando Cache...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </>
              )}
            </button>
            </div>
          </div>
        </form>
    );

  // Inline con section origen/visibilidad: solo el form (el shell + tabs lo provee la
  // sección padre "Tomas de Datos"). Sin ConfigSectionShell ni tab list propia.
  if (inline && (section === 'origen' || section === 'visibilidad')) {
    return <>{tabBody}</>;
  }

  // Inline General: carcasa unificada (icon+title = sidebar: Settings / "General") con tab list.
  if (inline) {
    return (
      <ConfigSectionShell
        icon={Settings}
        title="General"
        clientName={client.display_name || client.name}
        tabs={generalTabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as 'general' | 'history')}
      >
        {tabBody}
      </ConfigSectionShell>
    );
  }

  // Modal: card ad-hoc con header + tabs propios (uso desde ClientCard).
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Editar Cliente: {client.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('general')}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-1" /> General
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <History className="w-4 h-4 inline mr-1" /> Historial
            </button>
          </nav>
        </div>

        {tabBody}
      </div>
    </div>
  );
};

export default EditClientModal;